/**
 * Vocabulario del flujo de matricula nueva: las micro-pantallas, los cuatro
 * macro-pasos y la forma de lo que el usuario diligencia.
 *
 * Solo tipos y constantes. Donde esta el usuario y que falta lo decide
 * `MatriculaStore`, que es la unica maquina de pasos (Regla 3).
 */

/** Las micro-pantallas en las que se parte el flujo cuando la pantalla es angosta. */
export type MicroPantalla =
  | 'ident'
  | 'alumno'
  | 'contacto'
  | 'acudiente'
  | 'salud'
  | 'identidad'
  | 'eps'
  | 'firma'
  | 'foto'
  | 'revisar';

/**
 * Como se presenta el flujo: una micro-pantalla a la vez o el macro-paso
 * completo. Entra al store **como dato** — el store no consulta el DOM (AC-3).
 */
export type ModoFlujo = 'compacto' | 'amplio';

/** Las micro-pantallas de cada macro-paso, en orden. Es el `SUBS` del prototipo. */
export const MICRO_PANTALLAS: ReadonlyArray<ReadonlyArray<MicroPantalla>> = [
  ['ident', 'alumno', 'contacto', 'acudiente', 'salud'],
  ['identidad', 'eps', 'firma'],
  ['foto'],
  ['revisar'],
];

/** Cuatro. Escrito una sola vez para no repartir el numero por el codigo. */
export const TOTAL_MACROS = MICRO_PANTALLAS.length;

export const TITULOS_MACRO: readonly string[] = [
  'Datos del deportista',
  'Documentos',
  'Fotografía',
  'Revisar y enviar',
];

export const DETALLES_MACRO: readonly string[] = [
  'Identidad, contacto y salud',
  'Identidad, EPS y consentimiento',
  'Foto para el carnet',
  'Confirma y envía',
];

/** Los campos de texto y seleccion del formulario. */
export interface DatosMatricula {
  tipoDoc: string;
  numDoc: string;
  fechaNac: string;
  nombres: string;
  apellidos: string;
  genero: string;
  email: string;
  tel: string;
  dir: string;
  barrio: string;
  acuNombre: string;
  acuParentesco: string;
  acuDoc: string;
  acuTel: string;
  eps: string;
  sangre: string;
  emgNombre: string;
  emgTel: string;
  experiencia: string;
}

/** Arranca vacio: ni un dato del prototipo llega al codigo (Regla 11). */
export const DATOS_VACIOS: DatosMatricula = {
  tipoDoc: '',
  numDoc: '',
  fechaNac: '',
  nombres: '',
  apellidos: '',
  genero: '',
  email: '',
  tel: '',
  dir: '',
  barrio: '',
  acuNombre: '',
  acuParentesco: '',
  acuDoc: '',
  acuTel: '',
  eps: '',
  sangre: '',
  emgNombre: '',
  emgTel: '',
  experiencia: '',
};

export type EstadoCarga = 'idle' | 'busy' | 'ok' | 'error';

/**
 * Un archivo que ya vive en el servidor. Se guarda **la referencia**, nunca el
 * contenido: ni el binario del documento, ni la imagen de la firma, ni la foto
 * (Regla 4 y AC-12).
 *
 * Los cuatro metadatos existen para que la tarjeta **sobreviva a una recarga**
 * (AC-14 de SPEC-006). Con `{estado, referencia}` a secas, recargar a mitad del
 * paso 2 devolvia un `ok` sin nombre y sin ahorro: la tarjeta quedaba muda o
 * mentia. Son metadatos y se persisten; el contenido del archivo **no**.
 */
export interface Adjunto {
  estado: EstadoCarga;
  referencia: string | null;

  /** El nombre del archivo subido, tal y como se le ensena al usuario. */
  nombre: string | null;

  /** Su tipo real, que decide el icono de la tarjeta. */
  tipo: string | null;

  /** Los dos pesos, **medidos** en bytes. Nunca estimados (AC-4). */
  pesoOriginal: number | null;
  pesoFinal: number | null;
}

export const ADJUNTO_VACIO: Adjunto = {
  estado: 'idle',
  referencia: null,
  nombre: null,
  tipo: null,
  pesoOriginal: null,
  pesoFinal: null,
};

/** Lo que no es texto pero decide si una micro-pantalla esta completa. */
export interface EstadoMatricula {
  documentoIdentidad: Adjunto;
  certificadoEps: Adjunto;
  firma: Adjunto;
  fotoCarnet: Adjunto;
  emgUsaAcudiente: boolean;
  aceptaConsentimiento: boolean;
  aceptaReglamento: boolean;

  /**
   * La version del consentimiento que se acepto y el sello que devolvio el
   * servidor al firmarla (SPEC-007).
   *
   * Viven aqui, junto a `firma`, porque **tienen que sobrevivir a una
   * recarga**: la imagen del trazo no se guarda nunca (Regla 4), asi que sin el
   * sello y la referencia la pantalla no sabria que ya se firmo y volveria a
   * pedirlo. Ninguno decide si la pantalla esta completa —eso lo dice `firma`—;
   * son la prueba de que lo esta.
   */
  consentimientoVersion: number | null;
  consentimientoSello: string | null;
}

export const ESTADO_VACIO: EstadoMatricula = {
  documentoIdentidad: ADJUNTO_VACIO,
  certificadoEps: ADJUNTO_VACIO,
  firma: ADJUNTO_VACIO,
  fotoCarnet: ADJUNTO_VACIO,
  emgUsaAcudiente: true,
  aceptaConsentimiento: false,
  aceptaReglamento: false,
  consentimientoVersion: null,
  consentimientoSello: null,
};

/**
 * Donde se pinta cada error. Los adjuntos no comparten clave con los campos:
 * `eps` es el nombre de la EPS y `certificadoEps` el archivo.
 */
export type ClaveError =
  | keyof DatosMatricula
  | 'documentoIdentidad'
  | 'certificadoEps'
  | 'firma'
  | 'fotoCarnet'
  | 'reglamento';

export type ErroresMatricula = Partial<Record<ClaveError, string>>;
